import { createAppKit } from "@reown/appkit";
import { WagmiAdapter } from "@reown/appkit-adapter-wagmi";
import { mainnet } from "@reown/appkit/networks";
import { getAccount } from "@wagmi/core";
import { registerAppKitModal } from "../theme.js";
import { clearStakingSelection } from "../nftGallery.js";
import { getStakingEnv } from "../lib/env.js";
import { clearOwnedNftCache } from "../lib/alchemyNfts.js";
import { clearStakedTokenIdsCache } from "../lib/stakingData.js";
import { showToast } from "../lib/toast.js";
import { formatTxError } from "../lib/txErrors.js";
import {
  startWalletXpBalancePolling,
  syncWalletXpBalance,
} from "./walletBalance.js";

/** @type {import("@wagmi/core").Config | null} */
let wagmiConfigSingleton = null;

/** Wagmi config from AppKit — set after successful init. Used for reads/writes. */
export function getWagmiConfig() {
  return wagmiConfigSingleton;
}

/**
 * @param {`0x${string}` | null | undefined} address
 */
function emitAccount(address) {
  window.dispatchEvent(
    new CustomEvent("owlex:account", { detail: { address: address ?? null } })
  );
}

/** CAIP-2 id for Ethereum mainnet — must match `customRpcUrls` keys in WagmiAdapter. */
const ETHEREUM_MAINNET_CAIP = "eip155:1";

/**
 * Public Ethereum mainnet endpoints (no API key). Wagmi tries in order after optional user URL.
 * If one is rate-limited or down, the client can fall through (behavior depends on wagmi/Reown).
 */
/** Keep short — long lists slow wagmi’s first successful RPC when the first URL is down. */
const PUBLIC_MAINNET_RPC_FALLBACKS = [
  { url: "https://ethereum.publicnode.com" },
  { url: "https://cloudflare-eth.com" },
  { url: "https://rpc.ankr.com/eth" },
  { url: "https://eth.drpc.org" },
];

/**
 * Explicit RPC list so `eth_getBalance` in AppKit isn’t stuck on flaky defaults.
 * WagmiAdapter merges these first, then Reown Blockchain API, then viem chain RPCs.
 */
function ethereumMainnetRpcUrls() {
  const user = getStakingEnv().ethereumRpcUrl;
  if (user) {
    return [{ url: user }, ...PUBLIC_MAINNET_RPC_FALLBACKS];
  }
  return PUBLIC_MAINNET_RPC_FALLBACKS;
}

const walletStatEl = () => document.getElementById("stat-wallet");

function formatAddress(addr) {
  if (!addr || addr.length < 10) return addr;
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

/** @param {number} ms */
function waitMs(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Stale deploy cache / blocked chunk often surfaces as dynamic import + MIME errors.
 * @param {unknown} err
 */
function isChunkLoadFailure(err) {
  const msg = String(err?.message || err || "");
  return /dynamically imported module|chunkload|loading chunk|mime type|failed to fetch/i.test(
    msg
  );
}

/**
 * @param {Promise<unknown>} promise
 * @param {number} ms
 * @param {string} label
 */
async function withTimeout(promise, ms, label) {
  return Promise.race([
    promise,
    waitMs(ms).then(() => {
      throw new Error(label);
    }),
  ]);
}

/**
 * @param {HTMLButtonElement} connectBtn
 * @param {"disconnected" | "connected" | "loading" | "opening"} mode
 * @param {`0x${string}` | undefined} [address]
 */
function renderConnectButton(connectBtn, mode, address) {
  connectBtn.classList.toggle("btn-connect--connected", mode === "connected");
  if (mode === "connected" && address) {
    const short = formatAddress(address);
    connectBtn.innerHTML = `<span class="btn-connect__inner"><span class="btn-connect__dot" aria-hidden="true"></span><span class="btn-connect__addr">${short}</span></span>`;
    connectBtn.setAttribute("aria-label", `Wallet ${short} — open account`);
    connectBtn.title = address;
    return;
  }
  connectBtn.removeAttribute("title");
  if (mode === "loading") {
    connectBtn.textContent = "Loading…";
    connectBtn.setAttribute("aria-label", "Loading wallet");
    return;
  }
  if (mode === "opening") {
    connectBtn.textContent = "Opening…";
    connectBtn.setAttribute("aria-label", "Opening wallet");
    return;
  }
  connectBtn.textContent = "Connect wallet";
  connectBtn.setAttribute("aria-label", "Connect wallet");
}

/**
 * @param {import("@wagmi/core").Config} wagmiConfig
 * @param {HTMLButtonElement} connectBtn
 */
function syncWalletStatDisplay(wagmiConfig, connectBtn) {
  const { address } = getAccount(wagmiConfig);
  void syncWalletXpBalance(wagmiConfig);
  if (connectBtn.getAttribute("data-opening") === "1") return;
  if (address) {
    renderConnectButton(connectBtn, "connected", address);
  } else {
    renderConnectButton(connectBtn, "disconnected");
  }
}

/**
 * AppKit starts async work in the constructor; `open()` only works after `ready()`.
 * Without awaiting `ready()`, the connect button can appear to do nothing.
 */
export async function initWalletConnect() {
  const connectBtn = document.getElementById("connect-wallet");
  if (!connectBtn) return;

  const projectId = getStakingEnv().projectId;

  if (!projectId) {
    connectBtn.removeAttribute("disabled");
    connectBtn.addEventListener("click", () => {
      showToast(
        "Missing VITE_PROJECT_ID at build time.\n\nLocal: add it to staking/.env and run npm run build.\nCloudflare: set VITE_PROJECT_ID for production builds, then redeploy.",
        { variant: "info", duration: 12000 }
      );
    });
    return;
  }

  try {
    renderConnectButton(connectBtn, "loading");

    /** Ethereum mainnet only (chain id 1). */
    const networks = [mainnet];

    const wagmiAdapter = new WagmiAdapter({
      projectId,
      networks,
      customRpcUrls: {
        [ETHEREUM_MAINNET_CAIP]: ethereumMainnetRpcUrls(),
      },
    });

    const wagmiConfig = wagmiAdapter.wagmiConfig;
    wagmiConfigSingleton = wagmiConfig;
    startWalletXpBalancePolling(wagmiConfig);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("owlex:wagmi-ready"));
    }

    const origin =
      typeof window !== "undefined"
        ? window.location.origin
        : "https://playground.owlex.art";

    const metadata = {
      name: "Owlex",
      description: "Staking, playground, and inventory.",
      url: origin,
      icons: [`${origin}/gallery/logo.png`],
    };

    const modal = createAppKit({
      adapters: [wagmiAdapter],
      networks,
      metadata,
      projectId,
      features: {
        // Avoids extra third-party calls that can fail under strict CSP / ad blockers in production.
        analytics: false,
      },
    });

    registerAppKitModal(modal);

    /** Enable immediately — do not wait for `ready()` (can hang on slow networks; shell pages looked “stuck”). */
    connectBtn.removeAttribute("disabled");
    renderConnectButton(connectBtn, "disconnected");

    /** @type {string | null} */
    let lastEmittedAddress = null;

    function syncAccountFromWagmi() {
      const { address } = getAccount(wagmiConfig);
      const normalized = address ? address.toLowerCase() : null;
      if (!address) {
        clearOwnedNftCache();
        clearStakedTokenIdsCache();
        clearStakingSelection();
      }
      if (normalized !== lastEmittedAddress) {
        lastEmittedAddress = normalized;
        emitAccount(address);
      }
      syncWalletStatDisplay(wagmiConfig, connectBtn);
    }

    const modalReadyPromise = modal
      .ready()
      .then(() => {
        syncAccountFromWagmi();
        return true;
      })
      .catch((err) => {
        console.warn(
          "[owlex-staking] AppKit ready() delayed or failed (wallet may still work)",
          err
        );
        return false;
      });

    connectBtn.addEventListener("click", async () => {
      if (connectBtn.getAttribute("data-opening") === "1") return;
      connectBtn.setAttribute("data-opening", "1");
      connectBtn.disabled = true;
      renderConnectButton(connectBtn, "opening");
      try {
        // Never block clicks indefinitely on `ready()`; attempt open after a short grace period.
        await Promise.race([modalReadyPromise, waitMs(2200)]);
        const openPromise = modal.getIsConnectedState()
          ? modal.open({ view: "Account" })
          : modal.open({ view: "Connect", namespace: "eip155" });
        // If modal internals stall (observed on some desktop setups), recover button state instead of freezing.
        openPromise.catch((lateErr) => {
          console.warn("[owlex-staking] modal.open late rejection", lateErr);
        });
        await withTimeout(openPromise, 12000, "wallet modal open timeout");
        syncAccountFromWagmi();
      } catch (err) {
        console.error("[owlex-staking] modal.open", err);
        if (isChunkLoadFailure(err)) {
          showToast(
            "Wallet modal assets failed to load (likely stale cache after deploy). Hard refresh once (Ctrl/Cmd+Shift+R).",
            { variant: "error", duration: 9000 }
          );
        } else if (String(err?.message || "").includes("wallet modal open timeout")) {
          showToast(
            "Wallet popup timed out. Try once more; if it keeps happening, hard refresh (Ctrl/Cmd+Shift+R).",
            { variant: "error", duration: 9000 }
          );
        } else {
          showToast(formatTxError(err), { variant: "error" });
        }
      } finally {
        connectBtn.removeAttribute("data-opening");
        connectBtn.disabled = false;
        syncWalletStatDisplay(wagmiConfig, connectBtn);
      }
    });

    // Show address + emit owlex:account as soon as wagmi has state — do not block on AppKit `ready()` (often 5–30s on slow/CDN paths).
    syncAccountFromWagmi();

    modal.subscribeAccount(() => {
      // Must use the same path as initial sync: subscribeAccount fired very frequently
      // and was emitting owlex:account every time → full dashboard + log scans spam.
      syncAccountFromWagmi();
    });

    void modalReadyPromise;
  } catch (err) {
    console.error("[owlex-staking] AppKit initialization failed", err);
    renderConnectButton(connectBtn, "disconnected");
    connectBtn.removeAttribute("disabled");
    connectBtn.addEventListener("click", () => {
      const base = formatTxError(err);
      const hint =
        "\n\nIf you deployed to a new URL, add this origin in Reown Cloud → your project → Domains (allowlist).";
      showToast(base + hint, { variant: "error", duration: 12000 });
    });
  }
}

export function setWalletDisplay(text) {
  const el = walletStatEl();
  if (el) el.textContent = text;
}
